import {
  getShopProductArtifactSchema,
  getShopProductByEanArgsSchema,
  getShopProductByIsbnArgsSchema,
  getShopProductByExternalIdArgsSchema,
  getShopProductByProductIdArgsSchema,
  getShopProductContentSchema,
} from '@kanbu/schema/contracts';
import { Trans } from '@lingui/react/macro';
import { Gift, Package, Tag } from 'lucide-react';
import { motion } from 'motion/react';
import { useState } from 'react';
import { z } from 'zod';

import { createToolComponent } from '../../../toolRegistry';
import { Badge } from '../../badge/badge';
import { Button } from '../../button/button';
import { Card } from '../../ui';
import { ToolError } from '../components/ToolError';
import { ToolStatus } from '../components/ToolStatus';

export function Collapsible({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  if (isOpen) {
    return children;
  }

  return (
    <div className='space-y-2 max-h-32px relative'>
      <div className='absolute inset-0 bg-gradient-to-b from-transparent to-black/50' />
      <div className='overflow-hidden h-32'>{children}</div>
      <div className='absolute left-1/2 -translate-x-1/2 bottom-2'>
        <Button variant='outline' size='xs' onClick={() => setIsOpen(!isOpen)}>
          <Trans>Show more</Trans>
        </Button>
      </div>
    </div>
  );
}

export const GetShopProductTool = createToolComponent(
  {
    validate: false,
    schema: {
      args: z.union([
        getShopProductByProductIdArgsSchema,
        getShopProductByExternalIdArgsSchema,
        getShopProductByEanArgsSchema,
        getShopProductByIsbnArgsSchema,
      ]),
      artifact: getShopProductArtifactSchema,
      content: getShopProductContentSchema,
    },
  },
  ({ state, artifact, error, toolCall }) => {
    switch (state) {
      case 'call':
        return (
          <ToolStatus loading>
            <Trans>Fetching product details...</Trans>
          </ToolStatus>
        );

      case 'call-partial':
      case 'call-complete':
        return (
          <ToolStatus loading>
            <Trans>Parsing product details...</Trans>
          </ToolStatus>
        );

      case 'error':
        return (
          <ToolError message={<Trans>Product not found</Trans>} error={error} />
        );

      case 'result':
        return (
          <motion.div
            className='w-full'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Card.Root className='shadow-none overflow-hidden border border-chat-border'>
              <div className='relative h-36 w-full border-b border-chat-border bg-gray-50'>
                {artifact?.imageUrl && (
                  <img
                    src={artifact?.imageUrl}
                    alt={artifact?.name}
                    className='object-cover size-full'
                  />
                )}

                {/* Price badge */}
                <div className='absolute top-3 right-3'>
                  <Badge className='text-base font-bold px-3 py-1'>
                    {artifact?.price} Kč
                  </Badge>
                </div>
              </div>
              <Collapsible>
                <Card.Header>
                  <Card.Title className='text-xl'>
                    {artifact?.title || artifact?.name}
                  </Card.Title>
                </Card.Header>

                <Card.Content className='space-y-2'>
                  {/* Attributes section */}
                  {artifact?.attributes && artifact.attributes.length > 0 && (
                    <div className='space-y-2'>
                      <div className='flex items-center gap-2 text-chat-foreground font-semibold'>
                        <Tag size={16} />
                        <h3>
                          <Trans>Specifications</Trans>
                        </h3>
                      </div>
                      <div className='flex flex-col gap-y-2 text-sm'>
                        {artifact.attributes.map(attribute => (
                          <div
                            key={attribute.id}
                            className='flex justify-between gap-1'
                          >
                            <span className='text-muted-foreground font-semibold'>
                              {attribute.attribute.displayName}:
                            </span>
                            <span className='font-medium'>
                              {attribute.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Gifts section */}
                  {artifact?.gifts && artifact.gifts.length > 0 && (
                    <div className='space-y-2'>
                      <div className='flex items-center gap-2 text-chat-foreground font-semibold'>
                        <Gift size={16} />
                        <h3>
                          <Trans>Included gifts</Trans>
                        </h3>
                      </div>
                      <div className='space-y-1 ml-2'>
                        {artifact.gifts.map(gift => (
                          <div key={gift} className='flex items-center gap-2'>
                            <span className='px-2 py-1 bg-chat-background-secondary'>
                              {gift}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Accessories section */}
                  {artifact?.accessories && artifact.accessories.length > 0 && (
                    <div className='space-y-2'>
                      <div className='flex items-center gap-2 text-chat-foreground font-semibold'>
                        <Package size={16} />
                        <h3>
                          <Trans>Recommended accessories</Trans>
                        </h3>
                      </div>
                      <ul className='list list-disc text-sm ml-4'>
                        {artifact.accessories.map(accessory => (
                          <li key={accessory.id}>
                            <a
                              href={accessory.url}
                              className='hover:underline cursor-pointer'
                            >
                              {accessory.name}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className='flex justify-end pt-2'>
                    <Button variant='outline' size='sm' asChild>
                      <a
                        href={artifact?.url}
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        <Trans>View product details</Trans>
                      </a>
                    </Button>
                  </div>
                </Card.Content>
              </Collapsible>
            </Card.Root>
          </motion.div>
        );

      default:
        return null;
    }
  },
);
